/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldCheck, Star, MapPin, VideoIcon, Clock, Euro } from 'lucide-react';
import { getPublicDoctor } from '@/lib/sprint5';
import { ExternalLinks } from '@/components/profile/ExternalLinks';

const PublicDoctorProfilePage = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try { setData(await getPublicDoctor(slug)); }
    catch (e) { setError(e?.response?.data?.detail || 'Profilo non trovato'); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const seoScript = useMemo(() => {
    if (!data?.seo?.schema_org) return null;
    try {
      const s = JSON.parse(JSON.stringify(data.seo.schema_org));
      return JSON.stringify(s);
    } catch { return null; }
  }, [data]);

  // Inject SEO meta tags directly into <head> when data is loaded.
  useEffect(() => {
    if (!data?.seo) return;
    const originalTitle = document.title;
    document.title = data.seo.title || originalTitle;
    const meta = document.querySelector('meta[name="description"]');
    const originalDesc = meta?.getAttribute('content');
    if (meta) meta.setAttribute('content', data.seo.description || '');

    // JSON-LD script tag
    let script = null;
    if (seoScript) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = seoScript;
      script.setAttribute('data-sn-seo', 'doctor-profile');
      document.head.appendChild(script);
    }
    return () => {
      document.title = originalTitle;
      if (meta && originalDesc !== null && originalDesc !== undefined) meta.setAttribute('content', originalDesc);
      if (script && script.parentNode) script.parentNode.removeChild(script);
    };
  }, [data, seoScript]);

  if (error) return (
    <div className="max-w-3xl mx-auto p-8 text-center" data-testid="public-doctor-notfound">
      <h1 className="font-display text-2xl font-bold">Profilo non trovato</h1>
      <p className="text-sm text-muted-foreground mt-2">{error}</p>
      <Link to="/" className="inline-block mt-4 text-primary hover:underline">Torna alla home</Link>
    </div>
  );

  if (!data) return <div className="p-6"><Skeleton className="h-40 w-full" /></div>;

  const p = data.professional;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6" data-testid="public-doctor-page">
      {/* Hero */}
      <Card>
        <CardContent className="p-6 flex items-start gap-4">
          <div className="h-20 w-20 rounded-2xl bg-primary/10 text-primary flex items-center justify-center text-3xl font-display font-bold flex-shrink-0">
            {(p.full_name || 'D')[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display text-2xl font-bold">{p.full_name}</h1>
              {p.identity_verified && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[11px]" data-testid="public-verified-badge">
                  <ShieldCheck className="h-3 w-3" /> Verificato
                </Badge>
              )}
              {p.registry_verification_status === 'approved' && (
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[11px]">Albo verificato</Badge>
              )}
            </div>
            <div className="text-sm text-muted-foreground mt-1">{p.specialty}</div>
            {p.city && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="h-3 w-3" /> {p.city}</div>}
            <ExternalLinks websiteUrl={p.website_url} linkedinUrl={p.linkedin_url} className="mt-2" />
            {p.rating_avg && (
              <div className="flex items-center gap-1 mt-2" data-testid="public-rating">
                <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                <span className="font-semibold">{p.rating_avg}</span>
                <span className="text-xs text-muted-foreground">({p.rating_count} recensioni)</span>
              </div>
            )}
          </div>
          {p.telemedicine_available && (
            <Link to={`/telemedicine`} className="hidden md:block">
              <Button data-testid="public-book-btn"><VideoIcon className="h-4 w-4" /> Prenota visita</Button>
            </Link>
          )}
        </CardContent>
      </Card>

      {p.bio && (
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase font-semibold text-muted-foreground mb-1">Presentazione</div>
          <p className="text-sm whitespace-pre-wrap">{p.bio}</p>
        </CardContent></Card>
      )}

      {/* Services */}
      <section className="space-y-3">
        <h2 className="font-display font-semibold text-lg">Prestazioni disponibili</h2>
        {data.services.length === 0 ? (
          <Card><CardContent className="p-6 text-sm text-muted-foreground">Nessuna prestazione pubblica.</CardContent></Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="public-services">
            {data.services.map((s) => (
              <Card key={s.id}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-sm">{s.title}</div>
                    <Badge variant="outline" className="text-[10px]"><Euro className="h-2.5 w-2.5" />{s.price_eur}</Badge>
                  </div>
                  {s.description && <p className="text-xs text-muted-foreground">{s.description}</p>}
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <Clock className="h-3 w-3" /> {s.duration_minutes} min
                    {s.telemedicine && <><VideoIcon className="h-3 w-3 ml-1" /> Online</>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Reviews */}
      {data.reviews.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-display font-semibold text-lg">Recensioni</h2>
          <div className="space-y-2" data-testid="public-reviews">
            {data.reviews.map((r) => (
              <Card key={r.id}>
                <CardContent className="p-3 space-y-1">
                  <div className="flex items-center gap-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={`star-${r.id}-${i}`}
                        className={`h-3.5 w-3.5 ${i < Math.round(r.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`}
                      />
                    ))}
                    <span className="text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString('it')}</span>
                  </div>
                  {r.comment && <p className="text-sm whitespace-pre-wrap">{r.comment}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      )}

      {p.telemedicine_available && (
        <div className="md:hidden sticky bottom-4 left-0 right-0 px-4">
          <Link to="/telemedicine"><Button className="w-full shadow-elev-lg"><VideoIcon className="h-4 w-4" /> Prenota visita</Button></Link>
        </div>
      )}
    </div>
  );
};

export default PublicDoctorProfilePage;
